import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Asset, FileAccessApiService } from '@thehubz/client/data-access-api';
import { createVersionString } from '@thehubz/shared/model';
import { Observable, lastValueFrom, map } from 'rxjs';
import { ClientEnvironment, ENVIRONMENT } from '../environment';
import { AssetUIModel, UserSession } from '../models/';
import { USER_SESSION_TOKEN } from '../user-access/user-session.token';
import { StorageUtilityLib, createAssetFileKey } from '../util';

const SUPPORTED_IMAGE_EXTENSIONS = ['webp', 'png', 'jpg', 'jpeg', 'gif', 'svg'];

@Injectable({
  providedIn: 'root',
})
export class AssetsGenerateUrlService {
  private readonly userSession: UserSession;

  constructor(
    @Inject(ENVIRONMENT) private readonly env: ClientEnvironment,
    @Inject(USER_SESSION_TOKEN) userSessionToken: string,
    private fileAccessService: FileAccessApiService,
    private storageUtility: StorageUtilityLib,
    private httpClient: HttpClient,
  ) {
    this.userSession = this.storageUtility.getLocalStorage<UserSession>(userSessionToken);
  }

  getDownloadUrl(asset: AssetUIModel, version?: number) {
    const versionString = createVersionString(version || asset.mainFile.version);
    return `assets/${asset._id}/${versionString}/${asset.mainFile.defaultAspectRatio}/${'main.' + asset.mainFile.ext}`;
  }

  getMainUrl(asset: AssetUIModel, version?: number) {
    const overrideName = 'main.' + asset.mainFile?.ext;
    return this.env.fileAccessUrl + '/' + createAssetFileKey(asset, overrideName, false, version);
  }

  getThumbnailUrl(asset: AssetUIModel, overrideName?: string, version?: number) {
    return this.env.fileAccessUrl + '/' + createAssetFileKey(asset, overrideName || 'gen-thumbnail.webp', false, version);
  }

  getWorkflowChartUrl(workflowTypeId: number, noTokenFlag?: boolean) {
    let url = '';
    if (workflowTypeId) {
      url = this.env.fileAccessUrl + `/blob/tok/${this.userSession.access_token}/workflows/${workflowTypeId}/main.svg`;
      if (noTokenFlag) {
        url = this.env.fileAccessUrl + `/blob/workflows/${workflowTypeId}/main.svg`;
      }
    }
    return url;
  }

  getResizedUrl(asset: AssetUIModel, overrideName?: string, version?: number) {
    return this.env.fileAccessUrl + '/' + createAssetFileKey(asset, overrideName || 'gen-preview.webp', false, version);
  }

  /**
   * The file url to open natively in the browser.
   *
   * @param asset
   * @param version
   * @returns
   */
  getBrowserObjectUrl(asset: AssetUIModel, version?: number) {
    switch (asset.assetType) {
      case Asset.AssetTypeEnum.Video:
        return this.getVideoUrl(asset, version);
      case Asset.AssetTypeEnum.HtmlBanner:
        return this.getBannerUrl(asset, version);
      case Asset.AssetTypeEnum.Pdf:
        return this.getMainUrl(asset, version);
      default:
        return this.getResizedUrl(asset, undefined, version);
    }
  }

  getMainPresignedUrl(asset: AssetUIModel, name?: string, version?: number): Promise<string> {
    const key = createAssetFileKey(asset, name, false, version);
    return this.getPresignedUrl(key);
  }

  getMainPresignedUrl$(asset: AssetUIModel, name?: string, version?: number): Observable<string> {
    const key = createAssetFileKey(asset, name, false, version);
    return this.getPresignedUrl$(key);
  }

  getPresignedUrl(key: string): Promise<string> {
    return lastValueFrom(this.getPresignedUrl$(key));
  }

  private getPresignedUrl$(key: string): Observable<string> {
    if (!key) {
      throw new Error('Required parameter key was null or undefined when calling getPresignedUrl.');
    }

    // we need to build the api url manually here, because the endpoint is different in sharing client
    const params = new HttpParams().set('key', key).set('bucket', this.env.s3Config.bucket).set('expires', '60');
    return this.httpClient
      .request<{ presignedUrl: string }>('get', `${this.env.fileAccessUrl}/presignedurl`, {
        params,
      })
      .pipe(map((res) => res.presignedUrl));
  }

  getResizedPresignedUrl(asset: AssetUIModel, name?: string, version?: number): Promise<string> {
    return lastValueFrom(this.getResizedPresignedUrl$(asset, name, version));
  }

  getResizedPresignedUrl$(asset: AssetUIModel, name?: string, version?: number): Observable<string> {
    let key;
    if (asset.assetType === Asset.AssetTypeEnum.Image && !SUPPORTED_IMAGE_EXTENSIONS.includes(asset.mainFile.ext)) {
      const imagePreviewUrl = asset.previewUrl || this.generateImagePreview(asset, version).previewUrl;
      if (imagePreviewUrl) {
        key = imagePreviewUrl.substring(imagePreviewUrl.indexOf('assets'));
      }
    }

    if (asset.assetType === Asset.AssetTypeEnum.Video) {
      const videoPreviewUrl = asset.previewUrl || this.generateVideoPreview(asset, version).previewUrl;
      if (videoPreviewUrl) {
        key = videoPreviewUrl.substring(videoPreviewUrl.indexOf('assets'));
      }
    }

    if (asset.assetType === Asset.AssetTypeEnum.HtmlBanner) {
      const bannerPreviewURL = this.generateBannerPreview(asset, version).previewUrl;
      if (bannerPreviewURL) {
        key = bannerPreviewURL.substring(bannerPreviewURL.indexOf('assets'));
      }
    }

    // default, the main file ... might work
    if (!key) {
      key = createAssetFileKey(asset, name, false, version);
    }

    // call normal presigned url function for sharing client
    if (this.env.fileAccessUrl.includes('external-sharing')) {
      return this.getPresignedUrl$(key);
    }
    return this.fileAccessService.apiFileAccessControllerGetAssetPreviewUrl(key, this.env.s3Config.bucket).pipe(map((res) => res.presignedUrl));
  }

  getZoomImageUrl$(asset: AssetUIModel) {
    const key = createAssetFileKey(asset, 'gen-preview-xl.webp', false, asset.selectedVersion);
    if (this.env.fileAccessUrl.includes('external-sharing')) {
      return this.getPresignedUrl$(key);
    }
    return this.fileAccessService.apiFileAccessControllerGetAssetPreviewUrl(key, this.env.s3Config.bucket).pipe(map((res) => res.presignedUrl));
  }

  addPreviewUrl(asset: AssetUIModel, version?: number): AssetUIModel {
    if (asset) {
      switch (asset.assetType) {
        case Asset.AssetTypeEnum.Image:
          return this.generateImagePreview(asset, version);
        case Asset.AssetTypeEnum.Video:
          return this.generateVideoPreview(asset, version);
        case Asset.AssetTypeEnum.Pdf:
          return this.generatePdfPreview(asset, version);
        case Asset.AssetTypeEnum.HtmlBanner:
          return this.generateBannerPreview(asset, version);
        case Asset.AssetTypeEnum.Audio:
          return this.generateAudioPreview(asset, version);
        default:
          return this.generateOthersPreview(asset, version);
      }
    }
    return asset;
  }

  generatePreview(assets: AssetUIModel[]) {
    return assets?.map((asset) => {
      return this.addPreviewUrl(asset);
    });
  }

  private generateImagePreview(asset: AssetUIModel, version?: number) {
    let previewUrl;
    let mainUrl;
    let imageUrl;
    if (asset.mainFile?.name) {
      previewUrl = this.getResizedUrl(asset, '', version);
      mainUrl = this.getMainUrl(asset, version);
      imageUrl = this.getThumbnailUrl(asset, '', version);
    }
    return {
      ...asset,
      previewUrl,
      imageUrl,
      mainUrl,
      checked: false,
    };
  }

  private generateAudioPreview(asset: AssetUIModel, version?: number) {
    const previewUrl = this.getAudioUrl(asset, version);
    const mainUrl = this.getMainUrl(asset, version);
    return {
      ...asset,
      previewUrl,
      mainUrl,
      checked: false,
    };
  }

  private generateVideoPreview(asset: AssetUIModel, version?: number) {
    let previewUrl;
    let mainUrl;
    const imageUrl = this.getVideoThumbnailUrl(asset, version);
    if (asset.mainFile?.name) {
      previewUrl = this.getVideoUrl(asset, version);
      mainUrl = this.getMainUrl(asset, version);
    }
    return {
      ...asset,
      previewUrl,
      imageUrl,
      mainUrl,
      checked: false,
    };
  }

  private generatePdfPreview(asset: AssetUIModel, version?: number) {
    let previewUrl;
    let mainUrl;
    let imageUrl;
    if (asset.mainFile?.name) {
      previewUrl = this.getResizedUrl(asset, 'pdf', version);
      mainUrl = this.getMainUrl(asset, version);
      imageUrl = this.getThumbnailUrl(asset, '', version);
    }
    return {
      ...asset,
      previewUrl,
      imageUrl,
      mainUrl,
      checked: false,
    };
  }

  private generateBannerPreview(asset: AssetUIModel, version?: number) {
    let previewUrl;
    let mainUrl;
    let imageUrl = './assets/placeholder-image.png';
    if (asset.mainFile?.name) {
      mainUrl = this.getMainUrl(asset, version);
      previewUrl = this.getBannerUrl(asset, version);
      if (asset.mainFile.fallbackImg) {
        imageUrl = this.getFallbackUrl(asset, asset.mainFile.fallbackImg, version);
      }
    }
    return {
      ...asset,
      previewUrl,
      imageUrl,
      mainUrl,
      checked: false,
    };
  }

  private generateOthersPreview(asset: AssetUIModel, version?: number) {
    let mainUrl;
    if (asset.mainFile?.name) {
      mainUrl = this.getMainUrl(asset, version);
    }
    return {
      ...asset,
      mainUrl,
      checked: false,
    };
  }

  private getAudioUrl(asset: AssetUIModel, version?: number) {
    return this.env.fileAccessUrl + '/blob' + this.fileAccessToken() + '/' + createAssetFileKey(asset, 'gen-preview.mp3', false, version);
  }

  private getVideoUrl(asset: AssetUIModel, version?: number) {
    return this.env.fileAccessUrl + '/' + createAssetFileKey(asset, 'gen-preview.mp4', false, version);
  }

  private getVideoThumbnailUrl(asset: AssetUIModel, version?: number) {
    return this.env.fileAccessUrl + '/' + createAssetFileKey(asset, 'gen-thumbnail.0000000.jpg', false, version);
  }

  private getBannerUrl(asset: AssetUIModel, version?: number) {
    return this.env.fileAccessUrl + '/blob' + this.fileAccessToken() + '/' + createAssetFileKey(asset, 'index.html', false, version);
  }

  private getFallbackUrl(asset: AssetUIModel, name: string, version?: number) {
    return this.env.fileAccessUrl + this.fileAccessToken() + '/' + createAssetFileKey(asset, name, false, version);
  }

  private fileAccessToken() {
    return '/tok/' + this.userSession?.access_token;
  }
}
